import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  List,
  ListItem,
  Divider,
  Alert,
  Collapse,
  IconButton,
  Grid,
  Avatar,
  CardMedia,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useAuth } from 'services/auth/authContext';
import { getUserOrdersDetailed } from 'services/firebase/orderService';
import type { OrderWithBeerData } from 'services/firebase/orderService';
import styles from './styles';

const getStatusColor = (status: string) => {
  switch (status) {
    case 'pending':
      return 'warning';
    case 'confirmed':
      return 'info';
    case 'processing':
      return 'info';
    case 'shipped':
      return 'primary';
    case 'delivered':
      return 'success';
    case 'cancelled':
      return 'error';
    default:
      return 'default';
  }
};

const getStatusText = (status: string) => {
  switch (status) {
    case 'pending':
      return 'Pendente';
    case 'confirmed':
      return 'Confirmado';
    case 'processing':
      return 'Processando';
    case 'shipped':
      return 'Enviado';
    case 'delivered':
      return 'Entregue';
    case 'cancelled':
      return 'Cancelado';
    default:
      return status;
  }
};

const getPaymentStatusText = (status: string) => {
  switch (status) {
    case 'pending':
      return 'Pendente';
    case 'processing':
      return 'Processando';
    case 'approved':
      return 'Aprovado';
    case 'rejected':
      return 'Rejeitado';
    case 'cancelled':
      return 'Cancelado';
    case 'refunded':
      return 'Reembolsado';
    default:
      return status;
  }
};

const MyOrders: React.FC = () => {
  const { currentUser } = useAuth();
  const [orders, setOrders] = useState<OrderWithBeerData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());

  const toggleOrderExpansion = (orderId: string) => {
    setExpandedOrders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  useEffect(() => {
    const fetchOrders = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);
        const userOrders = await getUserOrdersDetailed(currentUser.uid);
        setOrders(userOrders);
      } catch (err) {
        console.error('Erro ao buscar pedidos:', err);
        setError('Erro ao carregar seus pedidos. Tente novamente mais tarde.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [currentUser]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (orders.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Meus Pedidos
        </Typography>
        <Alert severity="info">
          Você ainda não fez nenhum pedido. Que tal dar uma olhada na nossa
          loja?
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Meus Pedidos
      </Typography>

      <List>
        {orders.map((order, index) => (
          <React.Fragment key={order.id}>
            <ListItem sx={{ px: 0 }}>
              <Card sx={{ width: '100%' }}>
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Pedido #{order.orderNumber}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(order.createdAt).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 'bold', color: 'primary.main' }}
                      >
                        R$ {order.total.toFixed(2).replace('.', ',')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {order.items.length}{' '}
                        {order.items.length === 1 ? 'item' : 'itens'}
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        label={getStatusText(order.status)}
                        color={getStatusColor(order.status) as any}
                        size="small"
                      />
                      <Chip
                        label={`Pagamento: ${getPaymentStatusText(order.payment.status)}`}
                        variant="outlined"
                        size="small"
                      />
                    </Box>
                    <IconButton
                      onClick={() => toggleOrderExpansion(order.id)}
                      size="small"
                      sx={{ ml: 'auto' }}
                    >
                      {expandedOrders.has(order.id) ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </IconButton>
                  </Box>

                  <Collapse in={expandedOrders.has(order.id)}>
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, fontWeight: 'bold' }}
                      >
                        Itens do Pedido:
                      </Typography>
                      {order.items.map((item, itemIndex) => (
                        <Card
                          key={itemIndex}
                          variant="outlined"
                          sx={{ mb: 1, bgcolor: 'grey.50' }}
                        >
                          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={3} sm={2}>
                                {item.beerImage ? (
                                  // <Avatar
                                  //   src={item.beerImage}
                                  //   alt={item.beerName}
                                  //   sx={{ width: 60, height: 60 }}
                                  //   variant="rounded"
                                  // />
                                  <CardMedia
                                    component="img"
                                    height="200"
                                    image={item.beerImage}
                                    alt={item.beerName}
                                    sx={styles.productImage}
                                  />
                                ) : (
                                  <Avatar
                                    sx={{
                                      width: 60,
                                      height: 60,
                                      bgcolor: 'grey.300',
                                    }}
                                    variant="rounded"
                                  >
                                    🍺
                                  </Avatar>
                                )}
                              </Grid>
                              <Grid item xs={9} sm={10}>
                                <Box>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 'bold' }}
                                  >
                                    {item.beerName}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {item.brewery}
                                    {item.beerStyle && ` • ${item.beerStyle}`}
                                    {item.beerVolume &&
                                      ` • ${item.beerVolume}ml`}
                                    {item.beerAbv && ` • ${item.beerAbv}% ABV`}
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      mt: 1,
                                    }}
                                  >
                                    <Typography variant="body2">
                                      Quantidade: {item.quantity}
                                    </Typography>
                                    <Box sx={{ textAlign: 'right' }}>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        R${' '}
                                        {item.unitPrice
                                          .toFixed(2)
                                          .replace('.', ',')}{' '}
                                        cada
                                      </Typography>
                                      <Typography
                                        variant="subtitle2"
                                        sx={{
                                          fontWeight: 'bold',
                                          color: 'primary.main',
                                        }}
                                      >
                                        Total: R${' '}
                                        {item.totalPrice
                                          .toFixed(2)
                                          .replace('.', ',')}
                                      </Typography>
                                    </Box>
                                  </Box>
                                  {item.isSubscriberPrice &&
                                    item.originalUnitPrice && (
                                      <Typography
                                        variant="caption"
                                        color="success.main"
                                        sx={{ mt: 0.5, display: 'block' }}
                                      >
                                        Desconto de assinante aplicado! (De R${' '}
                                        {item.originalUnitPrice
                                          .toFixed(2)
                                          .replace('.', ',')}
                                        )
                                      </Typography>
                                    )}
                                </Box>
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>
                      ))}

                      {/* Informações de entrega */}
                      {/* <Box
                        sx={{
                          mt: 2,
                          p: 2,
                          bgcolor: 'grey.50',
                          borderRadius: 1,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 'bold', mb: 1 }}
                        >
                          Informações de Entrega:
                        </Typography>
                        <Typography variant="body2">
                          {order.shipping.method} - R${' '}
                          {order.shipping.cost.toFixed(2).replace('.', ',')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {order.shipping.address.street},{' '}
                          {order.shipping.address.number}
                          {order.shipping.address.complement &&
                            `, ${order.shipping.address.complement}`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {order.shipping.address.neighborhood},{' '}
                          {order.shipping.address.city} -{' '}
                          {order.shipping.address.state}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          CEP: {order.shipping.address.zipCode}
                        </Typography>
                        {order.shipping.trackingCode && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            Código de rastreamento:{' '}
                            <strong>{order.shipping.trackingCode}</strong>
                          </Typography>
                        )}
                      </Box> */}
                    </Box>
                  </Collapse>
                </CardContent>
              </Card>
            </ListItem>
            {index < orders.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );
};

export default MyOrders;
